import { Head, Link } from "@inertiajs/react"

import { EMPTY } from "@/components/products/fleet/format"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface Contact {
  name?: string
  email?: string
  phone?: string
  role?: string
}

interface LessorRow {
  id: number
  name: string
  active: boolean
  contacts: Contact[]
  notes: string | null
  active_riders: number
  cars: number
  open_work_orders: number
}

interface Props {
  lessors: LessorRow[]
}

export default function LessorsIndex({ lessors }: Props) {
  const routes = useOrgRoutes()

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Lessors", href: routes.lessorsPath() },
  ]

  const waiting = lessors.reduce((sum, l) => sum + l.open_work_orders, 0)

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Lessors" />

      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Lessors</h1>
            <p className="text-muted-foreground text-sm">
              {lessors.length} {lessors.length === 1 ? "lessor" : "lessors"}
              {waiting > 0 &&
                ` · ${waiting} open work ${waiting === 1 ? "order" : "orders"} waiting on a lessor right now`}
              .
            </p>
          </div>
          <Link
            href={routes.ridersPath()}
            className="text-sm underline-offset-4 hover:underline"
          >
            View all riders
          </Link>
        </div>

        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lessor</TableHead>
                <TableHead className="text-right">Active riders</TableHead>
                <TableHead className="text-right">Cars</TableHead>
                <TableHead className="text-right">Waiting on them</TableHead>
                <TableHead>Contacts</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {lessors.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    className="text-muted-foreground py-8 text-sm"
                  >
                    No lessors yet. They arrive with the first rider contract
                    the document pipeline reads.
                  </TableCell>
                </TableRow>
              ) : (
                lessors.map((lessor) => (
                  <TableRow key={lessor.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Link
                          href={routes.lessorPath(lessor.id)}
                          className="font-medium hover:underline"
                        >
                          {lessor.name}
                        </Link>
                        {!lessor.active && (
                          <Badge variant="secondary" className="text-xs">
                            Inactive
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {lessor.active_riders}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {lessor.cars}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {lessor.open_work_orders > 0 ? (
                        <span className="font-medium text-amber-600 dark:text-amber-400">
                          {lessor.open_work_orders}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{EMPTY}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <ContactSummary contacts={lessor.contacts} />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </AppLayout>
  )
}

function ContactSummary({ contacts }: { contacts: Contact[] }) {
  if (!contacts || contacts.length === 0) {
    return <span className="text-muted-foreground">{EMPTY}</span>
  }

  const [first, ...rest] = contacts
  const label = first.name || first.email || first.role || "Contact"

  return (
    <span>
      {label}
      {first.email && first.name && (
        <span className="text-muted-foreground"> · {first.email}</span>
      )}
      {rest.length > 0 && (
        <span className="text-muted-foreground"> +{rest.length} more</span>
      )}
    </span>
  )
}
